/**
 * contactTrackingConfig.js
 *
 * Mapa centralizado de ícones e cores para origens (UTM sources) e tipos de evento.
 *
 * Para adicionar uma nova origem:
 *   1. Adicione uma entrada em SOURCE_CONFIG com patterns, icon, dot e iconColor.
 *   2. O icon deve ser uma classe i-lucide-* (ver: https://lucide.dev/icons)
 *   3. dot = classes do nó circular (bg + border), iconColor = cor do ícone interno.
 *
 * Para adicionar um novo tipo de evento:
 *   1. Adicione uma entrada em EVENT_CONFIG com icon, bg e color.
 */

// ---------------------------------------------------------------------------
// Origens (UTM sources) — usadas na timeline de touchpoints dos pedidos
// ---------------------------------------------------------------------------
export const SOURCE_CONFIG = {
  google: {
    patterns: ['google'],
    icon:      'i-lucide-search',
    dot:       'bg-n-blue-3 border-n-blue-9',
    iconColor: 'text-n-blue-11',
    label:     'Google',
  },
  facebook: {
    patterns: ['facebook', 'fb', 'meta'],
    icon:      'i-lucide-thumbs-up',
    dot:       'bg-n-iris-3 border-n-iris-8',
    iconColor: 'text-n-iris-11',
    label:     'Facebook / Meta',
  },
  instagram: {
    patterns: ['instagram'],
    icon:      'i-lucide-camera',
    dot:       'bg-n-iris-3 border-n-iris-8',
    iconColor: 'text-n-iris-11',
    label:     'Instagram',
  },
  whatsapp: {
    patterns: ['whatsapp'],
    icon:      'i-lucide-message-circle',
    dot:       'bg-n-green-3 border-n-green-9',
    iconColor: 'text-n-green-11',
    label:     'WhatsApp',
  },
  linktree: {
    patterns: ['linktree', 'bio'],
    icon:      'i-ph-linktree-logo',
    dot:       'bg-n-amber-3 border-n-amber-9',
    iconColor: 'text-n-amber-11',
    label:     'Linktree',
  },
  tiktok: {
    patterns: ['tiktok'],
    icon:      'i-lucide-music',
    dot:       'bg-n-slate-3 border-n-slate-8',
    iconColor: 'text-n-slate-11',
    label:     'TikTok',
  },
  youtube: {
    patterns: ['youtube'],
    icon:      'i-lucide-youtube',
    dot:       'bg-n-ruby-3 border-n-ruby-8',
    iconColor: 'text-n-ruby-11',
    label:     'YouTube',
  },
  email: {
    patterns: ['email', 'newsletter', 'mailchimp', 'klaviyo'],
    icon:      'i-lucide-mail',
    dot:       'bg-n-blue-3 border-n-blue-7',
    iconColor: 'text-n-blue-10',
    label:     'E-mail',
  },
  direct: {
    patterns: ['direct'],
    icon:      'i-lucide-home',
    dot:       'bg-n-slate-3 border-n-slate-7',
    iconColor: 'text-n-slate-10',
    label:     'Direto',
  },
  // Fallback — exibido quando nenhum pattern acima bate
  default: {
    patterns: [],
    icon:      'i-lucide-globe',
    dot:       'bg-n-slate-3 border-n-slate-7',
    iconColor: 'text-n-slate-9',
    label:     'Outros',
  },
}

/**
 * Retorna a config de origem a partir do valor de utm_source.
 * Percorre SOURCE_CONFIG em ordem de declaração; retorna `default` se não bater.
 */
export function getSourceConfig(source) {
  if (!source) return SOURCE_CONFIG.direct
  const s = source.toLowerCase()
  for (const key of Object.keys(SOURCE_CONFIG)) {
    if (key === 'default') continue
    if (SOURCE_CONFIG[key].patterns.some(p => s.includes(p))) {
      return SOURCE_CONFIG[key]
    }
  }
  return SOURCE_CONFIG.default
}

// ---------------------------------------------------------------------------
// Tipos de evento — usados na timeline de eventos do contato
// ---------------------------------------------------------------------------
export const EVENT_CONFIG = {
  // Compras e comércio
  purchase:             { icon: 'i-lucide-receipt',             bg: 'bg-n-teal-3',   color: 'text-n-teal-11' },
  refund:               { icon: 'i-lucide-receipt-x',           bg: 'bg-n-ruby-3',   color: 'text-n-ruby-11' },
  add_to_cart:          { icon: 'i-lucide-shopping-cart',       bg: 'bg-n-amber-3',  color: 'text-n-amber-11' },
  add_to_wishlist:      { icon: 'i-lucide-heart',               bg: 'bg-n-ruby-3',   color: 'text-n-ruby-10' },
  begin_checkout:       { icon: 'i-lucide-shopping-bag',        bg: 'bg-n-amber-3',  color: 'text-n-amber-11' },
  add_shipping_info:    { icon: 'i-lucide-truck',               bg: 'bg-n-amber-3',  color: 'text-n-amber-10' },
  add_payment_info:     { icon: 'i-lucide-credit-card',         bg: 'bg-n-amber-3',  color: 'text-n-amber-11' },
  // Leads
  generate_lead:        { icon: 'i-lucide-user-plus',           bg: 'bg-n-blue-3',   color: 'text-n-blue-11' },
  qualify_lead:         { icon: 'i-lucide-user-check',          bg: 'bg-n-teal-3',   color: 'text-n-teal-11' },
  disqualify_lead:      { icon: 'i-lucide-user-x',              bg: 'bg-n-ruby-3',   color: 'text-n-ruby-11' },
  close_convert_lead:   { icon: 'i-lucide-badge-check',         bg: 'bg-n-teal-3',   color: 'text-n-teal-11' },
  close_unconvert_lead: { icon: 'i-lucide-badge-x',             bg: 'bg-n-solid-3',  color: 'text-n-slate-10' },
  // Navegação
  view_content:         { icon: 'i-lucide-monitor',             bg: 'bg-n-iris-3',   color: 'text-n-iris-11' },
  click:                { icon: 'i-lucide-mouse-pointer-click', bg: 'bg-n-teal-3',   color: 'text-n-teal-10' },
  page_view:            { icon: 'i-lucide-eye',                 bg: 'bg-n-blue-3',   color: 'text-n-blue-11' },
  session_start:        { icon: 'i-lucide-log-in',              bg: 'bg-n-solid-3',  color: 'text-n-slate-10' },
  search:               { icon: 'i-lucide-search',              bg: 'bg-n-solid-3',  color: 'text-n-slate-10' },
  // Fallback para eventos customizados não mapeados
  default:              { icon: 'i-lucide-zap',                 bg: 'bg-n-solid-3',  color: 'text-n-slate-10' },
}

/**
 * Retorna a config de evento a partir do event_type.
 * Retorna `default` se o tipo não for reconhecido (evento customizado).
 */
export function getEventConfig(type) {
  return EVENT_CONFIG[type] || EVENT_CONFIG.default
}
